import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Upload } from "lucide-react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";

const EMPTY = { name: "", description: "", price: "", category: "Kurti", images: [], sizes: ["S", "M", "L", "XL"], fabric: "", stock: 10, featured: false };

const AdminInput = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">{label}</span>
    <input {...props} className="mt-1.5 w-full bg-transparent border border-[#E5E1D8] rounded-none px-3 py-2.5 font-sans text-sm focus:border-[#1A1A1A] outline-none transition-colors" />
  </label>
);

const ProductForm = ({ initial, onSaved, onClose }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setForm((f) => ({ ...f, images: [...f.images, reader.result] }));
      reader.readAsDataURL(file);
    });
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Name and price are required."); return; }
    if (form.images.length === 0) { toast.error("Add at least one image."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock) || 0,
        sizes: typeof form.sizes === "string" ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : form.sizes,
      };
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success(form.id ? "Product updated." : "Product added.");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1A1A1A]/40 flex items-start justify-center overflow-y-auto p-4 md:p-10">
      <div className="bg-[#F9F8F6] border border-[#E5E1D8] w-full max-w-2xl" data-testid="product-form">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#E5E1D8]">
          <h3 className="font-serif text-2xl">{form.id ? "Edit Product" : "New Product"}</h3>
          <button onClick={onClose} data-testid="close-form"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <AdminInput label="Name" value={form.name} onChange={set("name")} data-testid="pf-name" />
          <label className="block">
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Description</span>
            <textarea value={form.description} onChange={set("description")} rows={3} className="mt-1.5 w-full bg-transparent border border-[#E5E1D8] rounded-none px-3 py-2.5 font-sans text-sm focus:border-[#1A1A1A] outline-none resize-none" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <AdminInput label="Price (INR)" type="number" value={form.price} onChange={set("price")} data-testid="pf-price" />
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Category</span>
              <select value={form.category} onChange={set("category")} data-testid="pf-category" className="mt-1.5 w-full bg-transparent border border-[#E5E1D8] rounded-none px-3 py-2.5 font-sans text-sm focus:border-[#1A1A1A] outline-none">
                <option value="Kurti">Kurti</option>
                <option value="Suit">Suit</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <AdminInput label="Fabric" value={form.fabric} onChange={set("fabric")} />
            <AdminInput label="Stock" type="number" value={form.stock} onChange={set("stock")} />
          </div>
          <AdminInput label="Sizes (comma separated)" value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes} onChange={set("sizes")} />

          <div>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Images</span>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-20 h-24 bg-[#F2EFE9] overflow-hidden">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) })} className="absolute top-0 right-0 bg-[#1A1A1A] text-white p-0.5"><X size={12} /></button>
                </div>
              ))}
              <label className="w-20 h-24 border border-dashed border-[#E5E1D8] flex flex-col items-center justify-center cursor-pointer hover:border-[#1A1A1A] transition-colors">
                <Upload size={16} className="text-[#595959]" />
                <span className="font-mono text-[8px] uppercase tracking-wider text-[#595959] mt-1">Upload</span>
                <input type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="pf-image-upload" />
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" />
            <span className="font-sans text-sm">Feature on homepage</span>
          </label>

          <button onClick={save} disabled={saving} data-testid="pf-save" className="w-full bg-[#1A1A1A] text-[#F9F8F6] py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#8C3A3A] transition-colors disabled:opacity-50">
            {saving ? "Saving…" : "Save Product"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data));
  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  const loadMessages = () => api.get("/admin/messages").then(({ data }) => setMessages(data));

  useEffect(() => { loadProducts(); loadOrders(); loadMessages(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted.");
    loadProducts();
  };

  const refund = async (id) => {
    await api.post(`/admin/orders/${id}/refund`);
    toast.success("Order refunded.");
    loadOrders();
  };

  const tabs = [["products", "Products"], ["orders", "Orders"], ["messages", "Messages"]];

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-24">
      <div className="flex justify-between items-end flex-wrap gap-4 mb-10">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C3A3A] mb-3">Dashboard</p>
          <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-[#1A1A1A]">Admin</h1>
        </div>
        {tab === "products" && (
          <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-product-btn" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F8F6] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#8C3A3A] transition-colors">
            <Plus size={15} /> Add Product
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-[#E5E1D8] mb-10">
        {tabs.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`} className={`font-mono text-[11px] uppercase tracking-[0.18em] px-5 py-3 transition-colors ${tab === v ? "bg-[#1A1A1A] text-[#F9F8F6]" : "text-[#595959] hover:text-[#8C3A3A]"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="border border-[#E5E1D8]" data-testid="admin-products">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-5 p-4 border-b border-[#E5E1D8] last:border-b-0">
              <div className="w-14 h-16 bg-[#F2EFE9] overflow-hidden shrink-0"><img src={p.images?.[0]} alt="" className="w-full h-full object-cover" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg text-[#1A1A1A] truncate">{p.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#595959]">{p.category} · {p.fabric} · stock {p.stock}{p.featured ? " · featured" : ""}</p>
              </div>
              <p className="font-mono text-sm">{inr(p.price)}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...p, sizes: p.sizes })} data-testid={`edit-${p.id}`} className="p-2 border border-[#E5E1D8] hover:border-[#1A1A1A] transition-colors"><Pencil size={14} /></button>
                <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="p-2 border border-[#E5E1D8] hover:border-[#8C3A3A] hover:text-[#8C3A3A] transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-[#E5E1D8]" data-testid="admin-orders">
          {orders.length === 0 && <p className="p-6 font-sans text-sm text-[#595959]">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} className="p-5 border-b border-[#E5E1D8] last:border-b-0">
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#1A1A1A]">#{o.id.slice(0, 8)} · {o.user_email}</p>
                  <p className="font-sans text-xs text-[#595959] mt-1">{o.shipping?.name} · {o.shipping?.city} · {o.shipping?.pincode}</p>
                  <p className="font-sans text-xs text-[#595959]">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm">{inr(o.total)}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C3A3A] mt-1">{o.status}</p>
                  {o.status === "confirmed" && (
                    <button onClick={() => refund(o.id)} data-testid={`refund-${o.id}`} className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] border border-[#E5E1D8] px-3 py-1.5 hover:border-[#8C3A3A] hover:text-[#8C3A3A] transition-colors">Issue Refund</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "messages" && (
        <div className="border border-[#E5E1D8]" data-testid="admin-messages">
          {messages.length === 0 && <p className="p-6 font-sans text-sm text-[#595959]">No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} className="p-5 border-b border-[#E5E1D8] last:border-b-0">
              <div className="flex justify-between flex-wrap gap-2">
                <p className="font-serif text-lg text-[#1A1A1A]">{m.subject || "(No subject)"}</p>
                <p className="font-mono text-[10px] text-[#595959]">{new Date(m.created_at).toLocaleDateString("en-IN")}</p>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C3A3A] mt-1">{m.name} · {m.email}</p>
              <p className="font-sans text-sm text-[#595959] mt-2">{m.message}</p>
            </div>
          ))}
        </div>
      )}

      {editing && <ProductForm initial={editing} onSaved={() => { setEditing(null); loadProducts(); }} onClose={() => setEditing(null)} />}
    </div>
  );
};

export default Admin;
